import React from 'react';
import { css, injectGlobal } from 'emotion';
import Helmet from 'react-helmet';
import Header from '../components/appComponents/Header';
import { PARTNERS_APP } from '../appLinks';

/* global tw */

injectGlobal(`.row{
  max-width: 82rem;
  @media (webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi){
      max-width: 75rem;
  }
}`);

const styles = {
    page: css`
        ${tw`w-screen relative`};
        background: #09080F;
        background-image:
            radial-gradient(ellipse 70% 50% at 50% 0%, rgba(124,58,237,0.14), transparent 65%),
            radial-gradient(ellipse 40% 30% at 90% 30%, rgba(201,168,76,0.06), transparent 70%),
            linear-gradient(180deg, #131119 0%, #09080F 100%);
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        min-height: 100vh;
        padding-bottom: 6rem;
    `,
    container: css`
        max-width: 82rem;
        ${tw`mx-auto px-6 lg:px-10 pt-16 lg:pt-24`};
    `,
    eyebrow: css`
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.18em;
        font-size: 0.75rem;
        color: #C9A84C;
        margin-bottom: 1rem;
    `,
    h1: css`
        font-weight: 800;
        font-size: 2.5rem;
        color: #F5F5F7;
        letter-spacing: -0.035em;
        line-height: 1.1;
        margin: 0 0 1rem 0;
        @media (min-width: 992px) { font-size: 3.5rem; }
        span {
            background: linear-gradient(135deg, #FFFFFF 0%, #CB53D1 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
        }
    `,
    lede: css`
        font-size: 1.0625rem;
        color: #BFBFD1;
        line-height: 1.75;
        max-width: 56rem;
        margin: 1rem 0 2.5rem 0;
        @media (min-width: 992px) { font-size: 1.125rem; }
    `,
    section: css`
        ${tw`mt-12 lg:mt-16`};
    `,
    h2: css`
        font-weight: 800;
        font-size: 1.75rem;
        color: #F5F5F7;
        letter-spacing: -0.025em;
        line-height: 1.15;
        margin: 0 0 1rem 0;
        @media (min-width: 992px) { font-size: 2.25rem; }
        &::after {
            content: '';
            display: block;
            margin-top: 0.875rem;
            height: 2px;
            width: 2.5rem;
            background: #C9A84C;
            border-radius: 2px;
        }
    `,
    h3: css`
        font-weight: 700;
        font-size: 1.25rem;
        color: #F0ECF9;
        letter-spacing: -0.015em;
        margin: 1.75rem 0 0.5rem 0;
        @media (min-width: 992px) { font-size: 1.4375rem; }
    `,
    h4: css`
        font-weight: 700;
        font-size: 1.0625rem;
        color: #C9A84C;
        margin: 1.25rem 0 0.5rem 0;
        letter-spacing: -0.01em;
    `,
    p: css`
        font-size: 1rem;
        color: #9898A8;
        line-height: 1.75;
        margin: 0.5rem 0;
        max-width: 52rem;
    `,
    cardGrid: css`
        ${tw`flex flex-wrap -mx-2 mt-4`};
    `,
    card: css`
        ${tw`mx-2 mb-4 p-5`};
        flex: 1 1 16rem;
        background: rgba(255, 255, 255, 0.025);
        border: 1px solid rgba(255, 255, 255, 0.06);
        border-radius: 14px;
        transition: all 0.25s ease;
        &:hover {
            border-color: rgba(255, 255, 255, 0.12);
            background: rgba(255, 255, 255, 0.04);
            transform: translateY(-2px);
            box-shadow: 0 12px 30px rgba(0, 0, 0, 0.35);
        }
        h5 {
            margin: 0 0 0.5rem 0;
            font-weight: 700;
            font-size: 1rem;
            color: #F5F5F7;
            letter-spacing: -0.01em;
        }
        p {
            margin: 0;
            font-size: 0.9375rem;
            color: #9898A8;
            line-height: 1.65;
        }
    `,
    list: css`
        ${tw`pl-0 my-4`};
        list-style: none;
        max-width: 52rem;
        li {
            position: relative;
            padding-left: 1.5rem;
            color: #BFBFD1;
            font-size: 1rem;
            line-height: 1.85;
            &::before {
                content: '●';
                position: absolute;
                left: 0;
                top: 0;
                color: #C9A84C;
                font-size: 0.625rem;
                line-height: 1.85;
            }
        }
    `,
    pillRow: css`
        ${tw`flex flex-wrap mt-3`};
        gap: 0.5rem;
    `,
    pill: css`
        display: inline-flex;
        align-items: center;
        padding: 0.5rem 0.875rem;
        border-radius: 9999px;
        background: rgba(255, 255, 255, 0.04);
        border: 1px solid rgba(255, 255, 255, 0.10);
        color: #F0ECF9;
        font-size: 0.875rem;
        font-weight: 500;
        letter-spacing: -0.005em;
    `,
    cta: css`
        ${tw`mt-12 lg:mt-16 p-8 lg:p-12`};
        background: linear-gradient(135deg, rgba(255, 255, 255, 0.03) 0%, rgba(170, 11, 202, 0.06) 100%);
        border: 1px solid rgba(255, 255, 255, 0.08);
        border-radius: 20px;
        text-align: center;
        h3 {
            font-weight: 800;
            font-size: 1.75rem;
            color: #F5F5F7;
            letter-spacing: -0.025em;
            margin: 0 0 0.75rem 0;
            @media (min-width: 992px) { font-size: 2.25rem; }
        }
        p {
            color: #BFBFD1;
            font-size: 1rem;
            line-height: 1.75;
            max-width: 38rem;
            margin: 0 auto;
        }
    `,
    storeRow: css`
        ${tw`flex flex-wrap justify-center items-center mt-6`};
        gap: 0.875rem;
    `,
    storeBtn: css`
        display: inline-flex;
        align-items: center;
        gap: 0.75rem;
        padding: 0.875rem 1.375rem;
        border-radius: 14px;
        background: #0A0A0F;
        border: 1px solid rgba(255, 255, 255, 0.10);
        color: #F5F5F7;
        text-decoration: none;
        font-family: 'Plus Jakarta Sans', 'Inter', sans-serif;
        transition: all 0.2s ease;
        min-width: 13.5rem;
        &:hover {
            border-color: rgba(255, 255, 255, 0.20);
            background: rgba(255, 255, 255, 0.04);
            transform: translateY(-1px);
            box-shadow: 0 10px 26px rgba(0, 0, 0, 0.45);
        }
        svg {
            width: 28px;
            height: 28px;
            flex-shrink: 0;
        }
        .meta {
            display: flex;
            flex-direction: column;
            align-items: flex-start;
            line-height: 1.1;
        }
        .meta .small {
            font-size: 0.6875rem;
            font-weight: 500;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #9898A8;
        }
        .meta .big {
            font-size: 1.125rem;
            font-weight: 700;
            letter-spacing: -0.01em;
            color: #F5F5F7;
        }
    `,
    closing: css`
        ${tw`mt-12 text-center`};
        h3 {
            font-weight: 800;
            font-size: 1.5rem;
            color: #F5F5F7;
            letter-spacing: -0.02em;
            margin: 0 0 0.5rem 0;
        }
        .brand {
            font-weight: 800;
            font-size: 2rem;
            background: linear-gradient(135deg, #FFFFFF 0%, #CB53D1 100%);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            background-clip: text;
            margin: 1.25rem 0 0.25rem 0;
        }
        .tagline {
            color: #9898A8;
            font-size: 1rem;
            font-style: italic;
        }
    `,
};

const VEHICLES = [
    'Toyota Innova Crysta',
    'Innova Hycross / Hybrid',
    'Kia Carnival',
    'Toyota Commuter',
    'Force Urbania',
    'Mercedes E-Class',
    'Mercedes S-Class',
    'BMW 5 Series',
    'BMW 7 Series',
    'Audi / Similar Premium Sedans',
    'Luxury SUVs / Premium Vans',
];

const BUSINESS_TYPES = [
    { title: 'Airport Transfers', desc: 'Premium pickups and drops for business and leisure travellers.' },
    { title: 'Corporate Travel', desc: 'Recurring bookings from companies and corporate accounts.' },
    { title: 'Executive Mobility', desc: 'Day-long executive travel and city movement.' },
    { title: 'Weddings & Events', desc: 'Multi-vehicle bookings for weddings, ceremonies and events.' },
    { title: 'Outstation Luxury Travel', desc: 'Inter-city journeys with premium chauffeur-driven cars.' },
    { title: 'VIP Guest Transportation', desc: 'High-priority guest movement with discreet, polished service.' },
];

const REASONS = [
    { title: 'Get More Business', desc: 'Tap into a steady stream of premium booking demand from airport, corporate, wedding, event, outstation and VIP segments.' },
    { title: 'Better Revenue Opportunities', desc: 'Premium customers expect quality service and are willing to pay for professionalism and reliability.' },
    { title: 'Technology Powered Platform', desc: 'Manage bookings, trip updates, payments and schedules through the Zyppys Partner App.' },
    { title: 'Expand Your Reach', desc: 'Get access to customers beyond your local network and grow into new cities with confidence.' },
];

const Partners = () => (
    <div className={styles.page}>
        <Helmet
            title="Partner with Zyppys – Grow Your Luxury Car Rental Business"
            meta={[
                { name: 'description', content: 'Partner with Zyppys to access premium customer bookings — airport transfers, corporate travel, weddings, VIP movement, events and executive travel across India.' },
                { name: 'keywords', content: 'Zyppys partner, luxury car rental partner, premium fleet partner, chauffeur driven cars, airport transfer partner, corporate travel partner, wedding car rental partner, outstation luxury travel' },
                { name: 'viewport', content: 'width=device-width, initial-scale=1, shrink-to-fit=no' },
                { property: 'og:title', content: 'Partner with Zyppys – Premium Mobility Network' },
                { property: 'og:description', content: 'Join India’s trusted premium mobility network. Get access to high-value bookings across airports, corporates, weddings and VIP travel.' },
                { property: 'og:type', content: 'website' },
                { property: 'og:site_name', content: 'Zyppys' },
            ]}
            link={[
                { rel: 'canonical', href: 'https://zyppys.com/partners/' },
                { rel: 'icon', type: 'image/png', href: '/favicon.png' },
            ]}
        />
        <Header iosUrl={PARTNERS_APP.ios} androidUrl={PARTNERS_APP.android} />

        <div className={styles.container}>
            <p className={styles.eyebrow}>For Fleet Partners</p>
            <h1 className={styles.h1}>Partner With <span>Zyppys</span></h1>
            <h2 className={css`
                font-weight: 600;
                font-size: 1.25rem;
                color: #F0ECF9;
                letter-spacing: -0.01em;
                line-height: 1.4;
                margin: 0 0 1.25rem 0;
                max-width: 52rem;
                @media (min-width: 992px) { font-size: 1.5rem; }
            `}>Grow Your Luxury Car Rental Business With Premium Bookings</h2>
            <p className={styles.lede}>
                Own a luxury car rental company? Join Zyppys and get access to premium customer bookings,
                corporate travel requests, airport transfers, weddings, VIP movement, events, and executive
                travel across India.
            </p>
            <p className={styles.lede}>
                Zyppys is building a trusted premium mobility network with high service standards and
                quality-focused fleet partners.
            </p>

            <section className={styles.section}>
                <h2 className={styles.h2}>Why Register With Zyppys?</h2>
                <div className={styles.cardGrid}>
                    {REASONS.map(item => (
                        <div key={item.title} className={styles.card}>
                            <h5>{item.title}</h5>
                            <p>{item.desc}</p>
                        </div>
                    ))}
                </div>

                <h3 className={styles.h3}>Receive bookings for</h3>
                <div className={styles.cardGrid}>
                    {BUSINESS_TYPES.map(item => (
                        <div key={item.title} className={styles.card}>
                            <h5>{item.title}</h5>
                            <p>{item.desc}</p>
                        </div>
                    ))}
                </div>
            </section>

            <section className={styles.section}>
                <h2 className={styles.h2}>Who Can Join?</h2>
                <p className={styles.p}>
                    We invite Luxury Car Rental Companies with professionally managed fleets.
                </p>

                <h3 className={styles.h3}>Accepted Vehicle Categories</h3>
                <p className={styles.p}>Vehicles must start from:</p>
                <div className={styles.pillRow}>
                    {VEHICLES.map(v => (
                        <span key={v} className={styles.pill}>{v}</span>
                    ))}
                </div>
            </section>

            <section className={styles.section}>
                <h2 className={styles.h2}>Zyppys Service Level Standards (Mandatory SLA)</h2>
                <p className={styles.p}>
                    To protect premium customer experience, every partner must maintain the following:
                </p>

                <h3 className={styles.h3}>Vehicle Standards</h3>
                <h4 className={styles.h4}>Excellent Condition Only</h4>
                <ul className={styles.list}>
                    <li>Clean interiors &amp; exterior</li>
                    <li>Fresh smell inside cabin</li>
                    <li>No dents, scratches, damage</li>
                    <li>AC in perfect working condition</li>
                    <li>Comfortable seats</li>
                    <li>Proper lighting &amp; electronics</li>
                </ul>

                <h4 className={styles.h4}>Vehicle Readiness</h4>
                <ul className={styles.list}>
                    <li>Fuelled and trip-ready</li>
                    <li>Valid permits, insurance &amp; documents</li>
                    <li>Punctual arrival before reporting time</li>
                </ul>

                <h3 className={styles.h3}>Chauffeur Standards</h3>
                <h4 className={styles.h4}>Professional Appearance</h4>
                <ul className={styles.list}>
                    <li>Well dressed / formal attire</li>
                    <li>Groomed and presentable</li>
                    <li>Polite communication</li>
                    <li>Customer-friendly attitude</li>
                </ul>

                <h4 className={styles.h4}>Driving Quality</h4>
                <ul className={styles.list}>
                    <li>Safe driving habits</li>
                    <li>Smooth driving experience</li>
                    <li>Route knowledge / navigation ready</li>
                </ul>

                <h3 className={styles.h3}>Customer Hospitality Standards</h3>
                <p className={styles.p}>Every trip should offer:</p>
                <ul className={styles.list}>
                    <li>Mobile Phone Charger</li>
                    <li>Mineral Water Bottle</li>
                    <li>Clean Napkins / Tissues</li>
                    <li>Clean cabin ambience</li>
                    <li>Assistance with luggage when required</li>
                </ul>

                <h3 className={styles.h3}>Operational SLA</h3>
                <ul className={styles.list}>
                    <li>Accept / reject trip requests quickly</li>
                    <li>Chauffeur details shared on time</li>
                    <li>Live trip status updates</li>
                    <li>Zero last-minute cancellations</li>
                    <li>Immediate escalation support if issue arises</li>
                    <li>Maintain punctuality at all times</li>
                </ul>
            </section>

            <section className={styles.section}>
                <h2 className={styles.h2}>Why Customers Prefer Zyppys Partners</h2>
                <p className={styles.p}>Because they receive:</p>
                <ul className={styles.list}>
                    <li>Premium vehicles</li>
                    <li>Reliable chauffeurs</li>
                    <li>Professional service</li>
                    <li>Clean &amp; comfortable rides</li>
                    <li>Consistent luxury experience</li>
                </ul>
            </section>

            <div className={styles.cta}>
                <h3>Download Zyppys Partner App Today</h3>
                <p>
                    Register your company, upload fleet details, and start receiving premium ride
                    requests.
                </p>
                <div className={styles.storeRow}>
                    <a
                        className={styles.storeBtn}
                        href="https://apps.apple.com/in/app/zyppys-partners/id6762341454"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Download Zyppys Partners on the App Store"
                    >
                        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden>
                            <path d="M16.365 1.43c0 1.14-.42 2.227-1.18 3.027-.81.86-2.13 1.527-3.22 1.44-.13-1.107.42-2.26 1.16-3.027.83-.867 2.24-1.5 3.24-1.44zM20.5 17.18c-.46 1.06-.69 1.54-1.29 2.48-.84 1.31-2.02 2.94-3.49 2.95-1.31.01-1.65-.86-3.43-.85-1.78.01-2.16.86-3.47.85-1.47-.01-2.59-1.49-3.43-2.8C2.99 16.49 2.74 12 4.05 9.87 5 8.34 6.51 7.42 7.93 7.42c1.45 0 2.36.85 3.56.85 1.16 0 1.87-.85 3.55-.85 1.27 0 2.62.69 3.58 1.89-3.15 1.73-2.64 6.23.88 7.87z"/>
                        </svg>
                        <span className="meta">
                            <span className="small">Download on the</span>
                            <span className="big">App Store</span>
                        </span>
                    </a>
                    <a
                        className={styles.storeBtn}
                        href="https://play.google.com/store/apps/details?id=com.doctorite.zyppyspartnerapp&hl=en_IN"
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label="Get Zyppys Partners on Google Play"
                    >
                        <svg viewBox="0 0 24 24" aria-hidden>
                            <path fill="#34A853" d="M3.609 1.814 13.792 12 3.609 22.186A2.001 2.001 0 0 1 3 20.764V3.236c0-.564.246-1.07.609-1.422z"/>
                            <path fill="#FBBC04" d="M16.81 8.99 6.05 2.866 3.61 1.814 13.79 12l3.02-3.01z"/>
                            <path fill="#EA4335" d="M3.61 22.186 16.81 15.01 13.79 12 3.61 22.186z"/>
                            <path fill="#4285F4" d="m20.16 10.872-3.35-1.882L13.79 12l3.02 3.01 3.35-1.882a1.5 1.5 0 0 0 0-2.256z"/>
                        </svg>
                        <span className="meta">
                            <span className="small">Get it on</span>
                            <span className="big">Google Play</span>
                        </span>
                    </a>
                </div>
            </div>

            <div className={styles.closing}>
                <h3>Join India’s Trusted Premium Mobility Network</h3>
                <div className="brand">Zyppys</div>
                <div className="tagline">One Platform. Countless Journeys.</div>
            </div>
        </div>
    </div>
);

export default Partners;
